import React, { useState, useCallback, useRef } from 'react';

import useGridApi from '../hooks/useGridApi.hook';
import BottomSlideIn from '../displayComponents/bottomSlideIn';

const noop = () => null;

const useBottomSlideInEditMode = ({
  getRowId,
  formToRender,
  renderBottomSlideIn,
  shouldCloseOnSave = true,
  shouldDeselectOnRowDataUpdated = true,
  useRowSelectionForEditPanel = true,
  onSave = () => null,
  mapRowForEditForm = values => values,
  getTitle = () => null,
  isCompactIconSticky = false,
}) => {
  const [rowToEdit, setRowToEdit] = useState(null);
  const [showBottomSlideIn, setShowBottomSlideIn] = useState(false);
  const { gridApi, onGridReady } = useGridApi();
  /**
   * True after "Add" until the user picks a grid row, dismisses the panel, or save closes it.
   * Ignores empty selection while adding (onAddClick's deselect, onRowDataUpdated deselect, etc.).
   */
  const inAddFlowRef = useRef(false);
  /** Synced ref so onRowDataUpdated handlers can tell if the edit panel is still open. */
  const showBottomSlideInRef = useRef(false);

  const handleRowSelection = useCallback(({ api }) => {
    const selectedRow = api.getSelectedRows()[0];

    if (inAddFlowRef.current && !selectedRow) {
      return;
    }

    if (!selectedRow) {
      showBottomSlideInRef.current = false;
      setShowBottomSlideIn(false);
      setRowToEdit(null);
      return;
    }

    inAddFlowRef.current = false;
    setRowToEdit(selectedRow);
    showBottomSlideInRef.current = true;
    setShowBottomSlideIn(true);
  }, []);

  const onAddClick = useCallback(() => {
    inAddFlowRef.current = true;
    setRowToEdit(null);
    showBottomSlideInRef.current = true;
    setShowBottomSlideIn(true);
    if (useRowSelectionForEditPanel) {
      gridApi?.deselectAll();
    }
  }, [gridApi, useRowSelectionForEditPanel]);

  const openEditPanel = useCallback(row => {
    inAddFlowRef.current = false;
    setRowToEdit(row);
    showBottomSlideInRef.current = true;
    setShowBottomSlideIn(true);
  }, []);

  /** Deselect only while the edit panel is open (e.g. after save refetch). */
  const hideBottomSlideIn = useCallback(() => {
    if (!showBottomSlideInRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      gridApi?.deselectAll();
    });
  }, [gridApi]);

  const userDismissBottomSlideIn = useCallback(() => {
    inAddFlowRef.current = false;
    showBottomSlideInRef.current = false;
    setShowBottomSlideIn(false);
    setRowToEdit(null);
    // Do not call gridApi.deselectAll() here — AG Grid 32 can drop all row data when
    // deselectAll runs while a row is still selected (e.g. select row → dismiss panel).
  }, []);

  const handleSave = useCallback(
    async values => {
      const selectedNode = useRowSelectionForEditPanel ? gridApi?.getSelectedNodes()[0] : null;

      if (useRowSelectionForEditPanel) {
        gridApi?.deselectAll();
      }

      await onSave({
        values,
        isEditMode: Boolean(rowToEdit),
      });

      if (shouldCloseOnSave) {
        inAddFlowRef.current = false;
        showBottomSlideInRef.current = false;
        setShowBottomSlideIn(false);
        setRowToEdit(null);
      }

      selectedNode?.setSelected(true);
    },
    [onSave, rowToEdit, shouldCloseOnSave, gridApi, useRowSelectionForEditPanel],
  );

  const bottomSlideInWithForm =
    renderBottomSlideIn && showBottomSlideIn ? (
      <BottomSlideIn hide={userDismissBottomSlideIn} isCompactIconSticky={isCompactIconSticky}>
        {formToRender({
          onSave: handleSave,
          values: mapRowForEditForm(rowToEdit),
          isEditMode: Boolean(rowToEdit),
          title: getTitle(rowToEdit),
        })}
      </BottomSlideIn>
    ) : null;

  return {
    gridApi,
    rowToEdit,
    showBottomSlideIn,
    openEditPanel,
    agGridProps: useRowSelectionForEditPanel
      ? {
          onSelectionChanged: handleRowSelection,
          onRowDataUpdated:
            shouldCloseOnSave && shouldDeselectOnRowDataUpdated ? hideBottomSlideIn : noop,
          getRowId,
          onGridReady,
          rowSelection: {
            mode: 'singleRow',
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: true,
          },
        }
      : {
          onRowDataUpdated: noop,
          getRowId,
          onGridReady,
          suppressRowClickSelection: true,
        },
    onAddClick,
    bottomSlideInWithForm,
  };
};

export default useBottomSlideInEditMode;
